import { actionProps } from "@/lib/controlAnalytics";
import { Link } from "react-router-dom";
import { useTheme } from "@jobenvoy/core";
import { useSession } from "@/auth/session";
import { EnvelopeIcon, MoonIcon, SignOutIcon, SunIcon } from "@/components/icons";
import { ROUTES } from "@/lib/routes";
import { useStanding } from "@/lib/hooks/standingContext";
import { envoyState } from "@/lib/standing";
import EnvoyDot from "@/components/site/EnvoyDot";

/** The account menu's actions; the Overview only on a phone, where the header has no nav. */
export default function AccountControls({ onNavigate }: { onNavigate?: () => void }) {
    const { theme, toggleTheme } = useTheme();
    const session = useSession();
    const standing = useStanding();
    const support = (
        <Link
            {...actionProps("navigation_clicked", {
                source: "AccountControls",
                target: ROUTES.SUPPORT,
            })}
            to={ROUTES.SUPPORT}
            onClick={onNavigate}
            aria-label="Support"
            className="inline-flex h-10 max-[760px]:min-h-11 min-w-10 items-center justify-center gap-2 whitespace-nowrap rounded-control border border-line-strong bg-card px-2.5 text-[11px] font-semibold text-fg transition-colors hover:bg-hover sm:px-3.5 sm:text-[13px]"
        >
            <EnvelopeIcon className="size-[17px] shrink-0" />
            <span>Support</span>
        </Link>
    );
    const appearance = (
        <button
            {...actionProps("theme_selected", {
                source: "AccountControls",
                option: theme === "dark" ? "light" : "dark",
            })}
            type="button"
            onClick={toggleTheme}
            aria-label="Switch between light and dark"
            className="inline-flex size-10 items-center justify-center gap-2 rounded-control border border-line-strong bg-card text-fg-muted transition-colors hover:bg-hover"
        >
            {theme === "dark" ? (
                <SunIcon className="size-[17px]" />
            ) : (
                <MoonIcon className="size-[17px]" />
            )}
            {theme === "dark" ? "Light mode" : "Dark mode"}
        </button>
    );
    const signOut = (
        <button
            {...actionProps("sign_out_clicked", { source: "AccountControls" })}
            type="button"
            onClick={session.signOut}
            className={`inline-flex h-10 max-[760px]:min-h-11 items-center gap-1.5 whitespace-nowrap rounded-control border border-line-strong bg-card px-2.5 text-[11px] font-semibold text-fg transition-colors sm:gap-2 sm:px-3.5 sm:text-[13px] hover:border-negative hover:bg-rejected hover:text-negative focus-visible:border-negative focus-visible:bg-rejected focus-visible:text-negative`}
        >
            <SignOutIcon className="size-3.5 shrink-0" />
            Sign out
        </button>
    );

    return (
        <div className="flex flex-col gap-1.5 [&>a]:h-11 [&>a]:justify-start [&>a]:text-[13px] [&>button]:h-11 [&>button]:w-full [&>button]:justify-start [&>button]:px-3.5 [&>button]:text-[13px]">
            {support}
            {appearance}
            <Link
                {...actionProps("navigation_clicked", {
                    source: "AccountControls",
                    target: ROUTES.OVERVIEW,
                })}
                to={ROUTES.OVERVIEW}
                onClick={onNavigate}
                // On a desktop the Overview is in the header's nav.
                className="flex items-center rounded-control px-3.5 font-semibold hover:bg-hover min-[761px]:hidden"
            >
                Overview
            </Link>
            <Link
                {...actionProps("navigation_clicked", {
                    source: "AccountControls",
                    target: ROUTES.ENVOY,
                })}
                to={ROUTES.ENVOY}
                onClick={onNavigate}
                className="flex items-center rounded-control px-3.5 font-semibold hover:bg-hover"
            >
                Your envoy
                {/* Its state at every width: the header's chip has no room under 1100 px. */}
                <span
                    data-testid="menu-envoy-state"
                    aria-hidden="true"
                    className="ml-auto flex items-center gap-1.5 text-[12px] font-medium text-fg-muted"
                >
                    <EnvoyDot on={standing.envoyOn} />
                    {envoyState(standing)}
                </span>
            </Link>
            <Link
                {...actionProps("navigation_clicked", {
                    source: "AccountControls",
                    target: ROUTES.ACCOUNT,
                })}
                to={ROUTES.ACCOUNT}
                onClick={onNavigate}
                className="flex items-center rounded-control px-3.5 font-semibold hover:bg-hover"
            >
                Account
            </Link>
            <Link
                {...actionProps("navigation_clicked", {
                    source: "AccountControls",
                    target: ROUTES.BILLING,
                })}
                to={ROUTES.BILLING}
                onClick={onNavigate}
                className="flex items-center rounded-control px-3.5 font-semibold hover:bg-hover"
            >
                Plan and billing
            </Link>
            <hr className="my-1 border-0 border-t border-line" />
            {signOut}
        </div>
    );
}
